import { useQuery } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'

export function useGamePassSummary() {
  const { user } = useAuth()
  const trpc = useTRPC()
  return useQuery({
    ...trpc.promoGamePassRouter.summary.queryOptions(),
    enabled: !!user && !user.isAnonymous,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  })
}

export function GamePassProfileBalance() {
  const query = useGamePassSummary()
  const { t, language } = useI18n()
  return (
    <section className="space-y-2 border-t pt-4" aria-label={t('gamePasses.title')} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium">{t('gamePasses.title')}</p>
        {query.data && (
          <p className="text-sm tabular-nums" data-testid="game-pass-profile-balance">
            {query.data.remaining}
          </p>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{t('gamePasses.shared')}</p>
      {!query.data && !query.isError && <p role="status" className="text-sm text-muted-foreground">{t('gamePasses.loading')}</p>}
      {query.isError && (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-destructive">{t('gamePasses.loadError')}</p>
          <Button type="button" size="sm" variant="outline" onClick={() => query.refetch()}>{t('gamePasses.retry')}</Button>
        </div>
      )}
    </section>
  )
}

export function GamePassBalanceCard() {
  const { t, language } = useI18n()
  const summary = useGamePassSummary()

  return (
    <Card id="game-passes" data-testid="game-pass-card" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <CardHeader>
        <CardTitle>{t('gamePasses.title')}</CardTitle>
        <CardDescription>{t('gamePasses.shared')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          {summary.data && <p className="text-lg font-semibold tabular-nums" data-testid="game-pass-balance">{summary.data.remaining}</p>}
          {!summary.data && !summary.isError && <p role="status" className="text-sm text-muted-foreground">{t('gamePasses.loading')}</p>}
        </div>
        {summary.isError && (
          <div className="space-y-2">
            <p role="alert" className="text-sm text-destructive">{t('gamePasses.loadError')}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => summary.refetch()}>{t('gamePasses.retry')}</Button>
          </div>
        )}
        <details className="text-sm text-muted-foreground">
          <summary id="game-pass-scope" className="cursor-pointer py-1">{t('gamePasses.scope')}</summary>
          <p className="mt-2 max-w-prose leading-relaxed">{t('gamePasses.details')}</p>
        </details>
      </CardContent>
    </Card>
  )
}
